import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useUIModalStore } from '../../stores/uiModalStore';
import { detectLang, useLanguage } from '../../hooks/useLanguage';
import type { Lang } from '../../hooks/useLanguage';
import './AuthModal.css';

const i18n: Record<string, Record<Lang, string>> = {
  welcomeBack:   { ja: 'おかえりなさい', en: 'Welcome back', ko: '다시 오신 것을 환영합니다', zh: '欢迎回来', es: 'Bienvenido de nuevo', fr: 'Bon retour', de: 'Willkommen zurück', pt: 'Bem-vindo de volta' },
  createAccount: { ja: 'アカウント作成', en: 'Create account', ko: '계정 만들기', zh: '创建账户', es: 'Crear cuenta', fr: 'Créer un compte', de: 'Konto erstellen', pt: 'Criar conta' },
  resetPassword: { ja: 'パスワードをリセット', en: 'Reset password', ko: '비밀번호 재설정', zh: '重置密码', es: 'Restablecer contraseña', fr: 'Réinitialiser le mot de passe', de: 'Passwort zurücksetzen', pt: 'Redefinir senha' },
  resetSent:     { ja: 'パスワードリセットメールを送信しました。受信箱を確認してください。', en: 'Password reset email sent! Check your inbox.', ko: '비밀번호 재설정 이메일을 보냈습니다! 받은편지함을 확인하세요.', zh: '密码重置邮件已发送！请检查收件箱。', es: '¡Email de restablecimiento enviado! Revisa tu bandeja.', fr: 'Email de réinitialisation envoyé ! Vérifiez votre boîte.', de: 'Zurücksetzungs-E-Mail gesendet! Überprüfen Sie Ihren Posteingang.', pt: 'Email de redefinição enviado! Verifique sua caixa de entrada.' },
  email:         { ja: 'メールアドレス', en: 'Email', ko: '이메일', zh: '电子邮件', es: 'Email', fr: 'Email', de: 'E-Mail', pt: 'Email' },
  password:      { ja: 'パスワード', en: 'Password', ko: '비밀번호', zh: '密码', es: 'Contraseña', fr: 'Mot de passe', de: 'Passwort', pt: 'Senha' },
  confirmPw:     { ja: 'パスワード確認', en: 'Confirm Password', ko: '비밀번호 확인', zh: '确认密码', es: 'Confirmar contraseña', fr: 'Confirmer le mot de passe', de: 'Passwort bestätigen', pt: 'Confirmar senha' },
  pwMismatch:    { ja: 'パスワードが一致しません', en: "Passwords don't match", ko: '비밀번호가 일치하지 않습니다', zh: '密码不匹配', es: 'Las contraseñas no coinciden', fr: 'Les mots de passe ne correspondent pas', de: 'Passwörter stimmen nicht überein', pt: 'As senhas não correspondem' },
  loading:       { ja: '読み込み中...', en: 'Loading...', ko: '로딩 중...', zh: '加载中...', es: 'Cargando...', fr: 'Chargement...', de: 'Laden...', pt: 'Carregando...' },
  signIn:        { ja: 'ログイン', en: 'Sign In', ko: '로그인', zh: '登录', es: 'Iniciar sesión', fr: 'Se connecter', de: 'Anmelden', pt: 'Entrar' },
  createBtn:     { ja: 'アカウント作成', en: 'Create Account', ko: '계정 만들기', zh: '创建账户', es: 'Crear cuenta', fr: 'Créer un compte', de: 'Konto erstellen', pt: 'Criar conta' },
  sendReset:     { ja: 'リセットリンクを送信', en: 'Send Reset Link', ko: '재설정 링크 보내기', zh: '发送重置链接', es: 'Enviar enlace', fr: 'Envoyer le lien', de: 'Link senden', pt: 'Enviar link' },
  orContinue:    { ja: 'または', en: 'or continue with', ko: '또는', zh: '或继续使用', es: 'o continuar con', fr: 'ou continuer avec', de: 'oder weiter mit', pt: 'ou continuar com' },
  forgotPw:      { ja: 'パスワードを忘れた場合', en: 'Forgot password?', ko: '비밀번호를 잊으셨나요?', zh: '忘记密码？', es: '¿Olvidaste tu contraseña?', fr: 'Mot de passe oublié ?', de: 'Passwort vergessen?', pt: 'Esqueceu a senha?' },
  noAccount:     { ja: 'アカウントをお持ちでないですか？', en: "Don't have an account?", ko: '계정이 없으신가요?', zh: '没有账户？', es: '¿No tienes cuenta?', fr: "Pas de compte ?", de: 'Kein Konto?', pt: 'Não tem uma conta?' },
  signUp:        { ja: '新規登録', en: 'Sign up', ko: '가입하기', zh: '注册', es: 'Registrarse', fr: "S'inscrire", de: 'Registrieren', pt: 'Cadastrar-se' },
  hasAccount:    { ja: 'すでにアカウントをお持ちですか？', en: 'Already have an account?', ko: '이미 계정이 있으신가요?', zh: '已有账户？', es: '¿Ya tienes cuenta?', fr: 'Déjà un compte ?', de: 'Schon ein Konto?', pt: 'Já tem uma conta?' },
  backToSignIn:  { ja: 'ログインに戻る', en: 'Back to sign in', ko: '로그인으로 돌아가기', zh: '返回登录', es: 'Volver a iniciar sesión', fr: 'Retour à la connexion', de: 'Zurück zur Anmeldung', pt: 'Voltar ao login' },
  magicLink:     { ja: 'メールでログインリンクを受け取る', en: 'Send me a magic link instead', ko: '대신 매직 링크 보내기', zh: '通过邮件接收登录链接', es: 'Enviarme un enlace mágico', fr: 'Envoyez-moi un lien magique', de: 'Magic-Link senden', pt: 'Receber link mágico por email' },
  magicSent:     { ja: 'メールにログインリンクを送信しました。受信箱を確認してください。', en: 'Magic link sent! Check your inbox to sign in.', ko: '매직 링크를 보냈습니다! 받은편지함에서 로그인하세요.', zh: '登录链接已发送！请查收邮件。', es: '¡Enlace mágico enviado! Revisa tu bandeja para iniciar sesión.', fr: 'Lien magique envoyé ! Vérifiez votre boîte pour vous connecter.', de: 'Magic-Link gesendet! E-Mail prüfen, um sich anzumelden.', pt: 'Link mágico enviado! Verifique sua caixa de entrada.' },
  magicNeedsEmail: { ja: 'メールアドレスを入力してください', en: 'Enter your email first', ko: '먼저 이메일을 입력하세요', zh: '请先输入邮箱', es: 'Ingresa tu email primero', fr: "Entrez d'abord votre email", de: 'Erst E-Mail eingeben', pt: 'Digite seu email primeiro' },
  activatePurchase: { ja: '購入した機能を使う', en: 'Use your purchase', ko: '구매한 기능 사용', zh: '使用已购买的功能', es: 'Usa tu compra', fr: 'Utilisez votre achat', de: 'Gekaufte Funktionen nutzen', pt: 'Use sua compra' },
  purchaseEmail: { ja: '決済時に入力したメールアドレスでログインしてください。パスワードがなくても、メールのリンクから利用を開始できます。', en: 'Sign in with the email you entered at checkout. You can use an email sign-in link without a password.', ko: '결제 시 입력한 이메일로 로그인하세요. 비밀번호 없이 이메일 로그인 링크를 사용할 수 있습니다.', zh: '请使用结账时填写的邮箱登录。无需密码，点击邮件中的登录链接即可开始使用。', es: 'Inicia sesión con el email que usaste al pagar. Puedes usar un enlace por email sin contraseña.', fr: 'Connectez-vous avec l’adresse e-mail utilisée au paiement. Un lien par e-mail suffit, sans mot de passe.', de: 'Melde dich mit der beim Kauf verwendeten E-Mail an. Du kannst einen Anmeldelink ohne Passwort nutzen.', pt: 'Entre com o email usado no pagamento. Você pode usar um link por email sem senha.' },
  sendLoginLink: { ja: 'ログインリンクを送信', en: 'Send sign-in link', ko: '로그인 링크 보내기', zh: '发送登录链接', es: 'Enviar enlace de acceso', fr: 'Envoyer un lien de connexion', de: 'Anmeldelink senden', pt: 'Enviar link de acesso' },
  usePassword: { ja: 'パスワードでログイン', en: 'Sign in with a password', ko: '비밀번호로 로그인', zh: '使用密码登录', es: 'Entrar con contraseña', fr: 'Connexion avec mot de passe', de: 'Mit Passwort anmelden', pt: 'Entrar com senha' },
};

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Optional contextual message shown under the title (e.g. "saving your
  // project needs a free account"). When present, the modal opens straight
  // into signup mode since the context is always framed as "create an
  // account to unlock X", not "sign back in".
  contextMessage?: string | null;
}

type AuthMode = 'login' | 'signup' | 'reset' | 'magic';

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, contextMessage }) => {
  const { lang: pageLang } = useLanguage();
  // Internal tool routes have no LanguageProvider. Use the saved/browser
  // language there, so an English buyer does not get a Japanese-only login.
  const internalToolRoute = typeof window !== 'undefined' && /^\/(editor|loop-forge)\/?$/.test(window.location.pathname);
  const lang = internalToolRoute ? detectLang() : pageLang;
  const purchaseMode = useUIModalStore((state) => state.authModalPurchase);
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const {
    isLoading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithGitHub,
    signInWithDiscord,
    signInWithMagicLink,
    resetPassword,
    clearError,
  } = useAuthStore();

  // Contextual prompts (save, etc.) are always pitched as "create an
  // account", so jump straight into signup mode whenever one opens with a
  // message attached — otherwise leave whatever mode the user was already in.
  useEffect(() => {
    if (!isOpen) return;
    setMode(purchaseMode ? 'magic' : contextMessage ? 'signup' : 'login');
    setMagicSent(false);
    setResetSent(false);
  }, [isOpen, contextMessage, purchaseMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      if (mode === 'magic') {
        await signInWithMagicLink(email);
        setMagicSent(true);
      } else if (mode === 'login') {
        await signInWithEmail(email, password);
        onClose();
      } else if (mode === 'signup') {
        if (password !== confirmPassword) {
          return;
        }
        await signUpWithEmail(email, password);
        onClose();
      } else if (mode === 'reset') {
        await resetPassword(email);
        setResetSent(true);
      }
    } catch (err) {
      // Error is handled in store
    }
  };

  const handleGoogleLogin = async () => {
    clearError();
    try {
      await signInWithGoogle();
    } catch (err) {
      // Error is handled in store
    }
  };

  const handleGitHubLogin = async () => {
    clearError();
    try {
      await signInWithGitHub();
    } catch (err) {
      // Error is handled in store
    }
  };

  const handleDiscordLogin = async () => {
    clearError();
    try {
      await signInWithDiscord();
    } catch (err) {
      // Error is handled in store
    }
  };

  const handleMagicLink = async () => {
    clearError();
    if (!email) return;
    try {
      await signInWithMagicLink(email);
      setMagicSent(true);
    } catch (err) {
      // Error is handled in store
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    clearError();
    setResetSent(false);
    setMagicSent(false);
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="auth-modal-close" onClick={onClose}>
          ×
        </button>

        <div className="auth-modal-header">
          <div className="auth-logo">
            <svg width="140" height="28" viewBox="0 0 140 28" fill="none">
              <text x="0" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#ffffff">L</text>
              <text x="16" y="22" fontSize="24" fontFamily="'Montserrat', sans-serif" fontWeight="700" letterSpacing="1" fill="#d4a853">A</text>
              <rect x="38" y="7" width="2" height="14" rx="1" fill="#d4a853"/>
              <text x="46" y="20" fontSize="14" fontFamily="'Montserrat', sans-serif" fontWeight="600" letterSpacing="3" fill="#8b7355">STUDIO</text>
            </svg>
          </div>
          <h2>
            {mode === 'login' && i18n.welcomeBack[lang]}
            {mode === 'signup' && i18n.createAccount[lang]}
            {mode === 'reset' && i18n.resetPassword[lang]}
            {mode === 'magic' && i18n.activatePurchase[lang]}
          </h2>
          {contextMessage && <p className="auth-modal-context">{contextMessage}</p>}
          {purchaseMode && <p className="auth-modal-context">{i18n.purchaseEmail[lang]}</p>}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {mode === 'magic' && magicSent ? (
          <div className="auth-success" role="status">{i18n.magicSent[lang]}</div>
        ) : mode === 'reset' && resetSent ? (
          <div className="auth-success">
            {i18n.resetSent[lang]}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label htmlFor="email">{i18n.email[lang]}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
            </div>

            {(mode === 'login' || mode === 'signup') && (
              <div className="auth-field">
                <label htmlFor="password">{i18n.password[lang]}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
              </div>
            )}

            {mode === 'signup' && (
              <div className="auth-field">
                <label htmlFor="confirmPassword">{i18n.confirmPw[lang]}</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                {password !== confirmPassword && confirmPassword && (
                  <span className="field-error">{i18n.pwMismatch[lang]}</span>
                )}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit"
              disabled={isLoading || (mode === 'signup' && password !== confirmPassword)}
            >
              {isLoading ? i18n.loading[lang] : (
                <>
                  {mode === 'login' && i18n.signIn[lang]}
                  {mode === 'signup' && i18n.createBtn[lang]}
                  {mode === 'reset' && i18n.sendReset[lang]}
                  {mode === 'magic' && i18n.sendLoginLink[lang]}
                </>
              )}
            </button>
          </form>
        )}

        {mode !== 'reset' && (
          <>
            <div className="auth-divider">
              <span>{i18n.orContinue[lang]}</span>
            </div>

            <div className="auth-social">
              <button
                type="button"
                className="auth-social-btn google"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>

              <button
                type="button"
                className="auth-social-btn github"
                onClick={handleGitHubLogin}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                GitHub
              </button>

              <button
                type="button"
                className="auth-social-btn discord"
                onClick={handleDiscordLogin}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4a.09.09 0 0 0-.07.03c-.18.33-.39.76-.53 1.09a16.09 16.09 0 0 0-4.8 0c-.14-.34-.35-.76-.54-1.09c-.01-.02-.04-.03-.07-.03c-1.5.26-2.93.71-4.27 1.33c-.01 0-.02.01-.03.02c-2.72 4.07-3.47 8.03-3.1 11.95c0 .02.01.04.03.05c1.8 1.32 3.53 2.12 5.24 2.65c.03.01.06 0 .07-.02c.4-.55.76-1.13 1.07-1.74c.02-.04 0-.08-.04-.09c-.57-.22-1.11-.48-1.64-.78c-.04-.02-.04-.08-.01-.11c.11-.08.22-.17.33-.25c.02-.02.05-.02.07-.01c3.44 1.57 7.15 1.57 10.55 0c.02-.01.05-.01.07.01c.11.09.22.17.33.26c.04.03.04.09-.01.11c-.52.31-1.07.56-1.64.78c-.04.01-.05.06-.04.09c.32.61.68 1.19 1.07 1.74c.03.01.06.02.09.01c1.72-.53 3.45-1.33 5.25-2.65c.02-.01.03-.03.03-.05c.44-4.53-.73-8.46-3.1-11.95c-.01-.01-.02-.02-.04-.02zM8.52 14.91c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.84 2.12-1.89 2.12zm6.97 0c-1.03 0-1.89-.95-1.89-2.12s.84-2.12 1.89-2.12c1.06 0 1.9.96 1.89 2.12c0 1.17-.83 2.12-1.89 2.12z"/>
                </svg>
                Discord
              </button>
            </div>

            {mode !== 'magic' && (magicSent ? (
              <div className="auth-success" style={{ marginTop: 16 }}>
                {i18n.magicSent[lang]}
              </div>
            ) : (
              <button
                type="button"
                className="auth-magic-link-btn"
                onClick={handleMagicLink}
                disabled={isLoading || !email}
                title={!email ? i18n.magicNeedsEmail[lang] : undefined}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {i18n.magicLink[lang]}
              </button>
            ))}
          </>
        )}

        <div className="auth-footer">
          {mode === 'magic' && (
            <button type="button" className="auth-link" onClick={() => switchMode('login')}>
              {i18n.usePassword[lang]}
            </button>
          )}
          {mode === 'login' && (
            <>
              <button type="button" className="auth-link" onClick={() => switchMode('reset')}>
                {i18n.forgotPw[lang]}
              </button>
              <span className="auth-footer-text">
                {i18n.noAccount[lang]}{' '}
                <button type="button" className="auth-link" onClick={() => switchMode('signup')}>
                  {i18n.signUp[lang]}
                </button>
              </span>
            </>
          )}
          {mode === 'signup' && (
            <span className="auth-footer-text">
              {i18n.hasAccount[lang]}{' '}
              <button type="button" className="auth-link" onClick={() => switchMode('login')}>
                {i18n.signIn[lang]}
              </button>
            </span>
          )}
          {mode === 'reset' && (
            <button type="button" className="auth-link" onClick={() => switchMode('login')}>
              {i18n.backToSignIn[lang]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
